import React from "react";

export const TERMS = <div>
  <h1>Terms of Use for WannaCollab.io</h1>
  <p><strong>Last Updated: 12/06/24</strong></p>
  <p>Welcome to WannaCollab.io (&quot;WannaCollab,&quot; &quot;we,&quot; &quot;our,&quot; or &quot;us&quot;). These Terms of Use (&quot;Terms&quot;) govern your access to and use of the WannaCollab website and services (the &quot;Platform&quot;). By accessing or using the Platform, you agree to be bound by these Terms. If you do not agree, you must not use the Platform.</p>

  <h2>1. Acceptance of Terms</h2>
  <p>By accessing, browsing, or using the Platform, you confirm that you are at least 18 years old (or the age of majority in your jurisdiction) and have the legal capacity to enter into this agreement. If you do not meet these requirements, you must not use the Platform.</p>

  <h2>2. Description of Services</h2>
  <p>WannaCollab is a social networking platform designed to connect influencers and creators for collaboration opportunities. We are not responsible for any interactions, agreements, or outcomes that occur between users.</p>

  <h2>3. Account Registration</h2>
  <p>To use WannaCollab, you must create an account and provide accurate and complete information. You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account. We reserve the right to suspend or terminate accounts for violation of these Terms or for any activity we deem inappropriate.</p>

  <h2>4. User Conduct</h2>
  <p>You agree not to:</p>
  <ul>
    <li>Use the Platform for unlawful purposes or to violate any applicable laws or regulations.</li>
    <li>Impersonate another person, provide false information, or engage in misleading behavior.</li>
    <li>Harass, threaten, or harm other users.</li>
    <li>Upload or share content that is offensive, defamatory, obscene, or otherwise inappropriate.</li>
    <li>Attempt to access or interfere with the Platform&apos;s security or functionality.</li>
  </ul>

  <h2>5. Disclaimer of Responsibility for User Interactions</h2>
  <p>WannaCollab provides a platform for users to connect but does not vet, endorse, or monitor users. You acknowledge that:</p>
  <ul>
    <li>Any collaboration or meeting arranged through the Platform is at your own discretion and risk.</li>
    <li>WannaCollab is not responsible for any disputes, agreements, or issues that arise between users, including but not limited to financial losses, safety concerns, or breaches of contract.</li>
    <li>You are solely responsible for exercising caution, conducting due diligence, and ensuring your safety when interacting with other users.</li>
  </ul>

  <h2>6. Content Ownership and Use</h2>
  <p><strong>User-Generated Content:</strong> By posting or sharing content on WannaCollab, you grant us a non-exclusive, royalty-free, worldwide license to use, display, and distribute your content solely for the purpose of operating and promoting the Platform.</p>
  <p><strong>WannaCollab Content:</strong> All Platform design, features, logos, and text are owned by WannaCollab or its licensors and are protected by intellectual property laws. You may not copy, modify, or distribute any WannaCollab content without prior written consent.</p>

  <h2>7. Privacy</h2>
  <p>Your use of WannaCollab is subject to our Privacy Policy, which explains how we collect, use, and protect your data. By using the Platform, you consent to these practices.</p>

  <h2>8. Limitation of Liability</h2>
  <p>To the fullest extent permitted by law, WannaCollab shall not be liable for:</p>
  <ul>
    <li>Any direct, indirect, incidental, special, or consequential damages arising from your use of the Platform.</li>
    <li>Any disputes, injuries, or losses resulting from interactions with other users.</li>
    <li>Unauthorized access to or alteration of your account or data.</li>
  </ul>

  <h2>9. Indemnification</h2>
  <p>You agree to indemnify and hold WannaCollab harmless from any claims, damages, losses, or expenses (including legal fees) arising out of your use of the Platform, violation of these Terms, or interaction with other users.</p>

  <h2>10. Modifications to the Platform or Terms</h2>
  <p>We reserve the right to modify, suspend, or discontinue the Platform at any time without notice. We may also update these Terms from time to time. Your continued use of the Platform after updates constitutes your acceptance of the revised Terms.</p>

  <h2>11. Termination</h2>
  <p>We may suspend or terminate your account or access to the Platform at any time if you violate these Terms or for any reason we deem appropriate.</p>

  <h2>12. Governing Law</h2>
  <p>These Terms are governed by the laws of Nevada, without regard to its conflict of laws principles. Any disputes will be resolved exclusively in the courts of Nevada.</p>

  <h2>13. Contact Information</h2>
  <p>If you have any questions or concerns about these Terms, please contact us at:</p>
  <p><strong>Email:</strong> team@wannacollab.io</p>

  <p>By using WannaCollab, you acknowledge that you have read, understood, and agreed to these Terms of Use.</p>
</div>;


export const PRIVACY = <div>
  <h1>Privacy Policy for WannaCollab.io</h1>
  <p><strong>Last Updated: 12/06/24</strong></p>
  <p>WannaCollab.io (“WannaCollab,” “we,” “us,” or “our”) respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, share, and safeguard your information when you use WannaCollab.io (the “Platform”). By using the Platform, you consent to the practices described in this Privacy Policy.</p>

  <h2>1. Information We Collect</h2>
  <h3>a. Information You Provide to Us</h3>
  <p>We collect information you voluntarily provide when using the Platform, including:</p>
  <ul>
    <li><strong>Account Information:</strong> Name, email address, username, password, profile photo, and other details necessary for your profile.</li>
    <li><strong>Content You Share:</strong> Messages, posts, photos, and other information you upload or share on the Platform.</li>
    <li><strong>Payment Information:</strong> If applicable, payment details are collected securely via our third-party payment processors.</li>
  </ul>

  <h3>b. Information We Collect Automatically</h3>
  <p>We may collect information automatically, including:</p>
  <ul>
    <li><strong>Device Information:</strong> Details about the device you use to access the Platform (e.g., IP address, browser type, operating system).</li>
    <li><strong>Usage Information:</strong> Data about your activity on the Platform, including pages viewed, links clicked, and session duration.</li>
    <li><strong>Cookies and Tracking Technologies:</strong> We use cookies and similar technologies to enhance your experience, analyze usage, and improve our services.</li>
  </ul>

  <h3>c. Information from Third Parties</h3>
  <p>We may receive information about you from third-party sources, such as social media platforms if you link your account.</p>

  <h2>2. How We Use Your Information</h2>
  <p>We use your information to:</p>
  <ul>
    <li>Provide, operate, and improve the Platform.</li>
    <li>Facilitate connections and collaborations between users.</li>
    <li>Personalize your experience and content.</li>
    <li>Send transactional communications, such as account notifications and updates.</li>
    <li>Respond to your inquiries or provide customer support.</li>
    <li>Ensure compliance with our Terms of Use and policies.</li>
  </ul>

  <h2>3. How We Share Your Information</h2>
  <p>We do not sell your personal information. We may share your information in the following ways:</p>
  <ul>
    <li><strong>With Other Users:</strong> Information you include in your profile or content you share may be visible to other users.</li>
    <li><strong>With Service Providers:</strong> Third-party vendors help us operate the Platform (e.g., hosting, payment processing, analytics). These providers are required to safeguard your information.</li>
    <li><strong>For Legal Compliance:</strong> We may disclose information if required to comply with applicable laws, regulations, or legal processes.</li>
    <li><strong>In Case of a Business Transfer:</strong> If WannaCollab undergoes a merger, acquisition, or sale of assets, your information may be transferred to the new entity.</li>
  </ul>

  <h2>4. Your Choices</h2>
  <p>You have control over your information and can:</p>
  <ul>
    <li><strong>Access and Update Information:</strong> Edit your profile or account settings to update your information.</li>
    <li><strong>Manage Cookies:</strong> Adjust your browser settings to disable cookies, though this may limit certain features.</li>
    <li><strong>Delete Your Account:</strong> Contact us at team@wannacollab.io to delete your account. Note that some information may remain archived as required by law.</li>
  </ul>

  <h2>5. Data Retention</h2>
  <p>We retain your information as long as necessary to provide the Platform and fulfill the purposes outlined in this Privacy Policy. We may retain certain data to comply with legal obligations or resolve disputes.</p>

  <h2>6. Security</h2>
  <p>We implement industry-standard measures to protect your information from unauthorized access, disclosure, or loss. However, no system is completely secure, and we cannot guarantee absolute security.</p>

  <h2>7. Children’s Privacy</h2>
  <p>The Platform is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If we become aware that a child’s information has been collected, we will take steps to delete it.</p>

  <h2>8. International Users</h2>
  <p>By using the Platform, you consent to the processing of your information in the United States or other countries where we or our service providers operate. These countries may not have the same data protection laws as your home jurisdiction.</p>

  <h2>9. Third-Party Links</h2>
  <p>The Platform may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies before sharing your information.</p>

  <h2>10. Changes to This Privacy Policy</h2>
  <p>We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of significant changes by posting a notice on the Platform or sending an email. Continued use of the Platform after updates constitutes acceptance of the revised policy.</p>

  <h2>11. Contact Us</h2>
  <p>If you have any questions, concerns, or requests about this Privacy Policy, please contact us at:</p>
  <p><strong>Email:</strong> team@wannacollab.io</p>

  <p>By using WannaCollab, you acknowledge that you have read, understood, and agree to this Privacy Policy.</p>
</div>;